// @flow
import { boolean, string, number } from 'yup';
import {
  CLUSTER_RESOURCES_GRID_FIELDNAMES,
  CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES,
  CLUSTER_WORKLOADS_INPUTS_FIELDNAMES,
} from '@streaming-projects/orgs/enums';
import { convertConfigArrayToMap } from '@src/configuration/utils';
import {
  TEXT_NUMBER_INPUT_TYPE,
  CHECKBOX_INPUT_TYPE,
  SELECT_INPUT_TYPE,
} from '@src/formik-utils/consts';
import {
  clusterMonthlyDataInputTransformationFunc,
  connectorsMonthlyDataInputTransformationFunc,
} from '@components/cluster/cluster-input-table/utils';
import {
  CLUSTER_AVAILABLE_CONNECTORS_LIST,
  CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_NETWORKING_TYPE_LABEL_BACKEND_NAME,
  CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST,
  CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST_BACKEND_NAME,
  CONNECTOR_LABEL,
  ENTERPRISE_CLUSTER_TYPE,
} from '@src/constants';
import { FORM_NAMES } from '@streaming-projects/enums';
import { IS_TABLE_FLOW_LIVE } from '@streaming-projects/constants';
import { infiniteRetentionInputValidationFunc } from '@streaming-projects/utils';

import { SP_MAX_INPUT_VALUE_FOR_ANY_FIELD } from './constants';

export const CLUSTER_WORKLOAD_ENABLE_DISABLE_CONFIG = {
  displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.IS_ENABLED,
  backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.IS_ENABLED,
  validation: boolean().label(CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.IS_ENABLED).required(),
};

export const CLUSTER_WORKLOAD_DETAILS_INPUT_CONFIG = [
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.WORKLOAD_NAME,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.WORKLOAD_NAME,
    validation: string().label(CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.WORKLOAD_NAME).required(),
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CLUSTER_RETENTION,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION,
    icon: null,
    validation: infiniteRetentionInputValidationFunc,
    inputType: TEXT_NUMBER_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CLUSTER_RETENTION_INFINITE,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION_INFINITE,
    icon: null,
    inputType: CHECKBOX_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.FOLLOWER_FETCH,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.FOLLOWER_FETCH,
    icon: null,
    inputType: CHECKBOX_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.EXISTING_STORAGE,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.EXISTING_STORAGE,
    icon: null,
    validation: number()
      .min(0)
      .max(SP_MAX_INPUT_VALUE_FOR_ANY_FIELD)
      .label(CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.EXISTING_STORAGE)
      .required()
      .typeError('You must specify a number'),
    inputType: TEXT_NUMBER_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CKU,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.CKU,
    icon: null,
    validation: number()
      .min(0)
      .max(SP_MAX_INPUT_VALUE_FOR_ANY_FIELD)
      .label(CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CKU)
      .required()
      .typeError('You must specify a number'),
    inputType: TEXT_NUMBER_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  },
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CLUSTER_WORKLOAD_NETWORKING_TYPE,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_WORKLOAD_NETWORKING_TYPE,
    icon: null,
    inputType: SELECT_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
    validation: ({ clusterType }) => {
      if (clusterType === ENTERPRISE_CLUSTER_TYPE) {
        return string()
          .required()
          .label(CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.CLUSTER_WORKLOAD_NETWORKING_TYPE)
          .nullable();
      }
      return string().nullable();
    },
  },
];

if (IS_TABLE_FLOW_LIVE) {
  CLUSTER_WORKLOAD_DETAILS_INPUT_CONFIG.push({
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.IS_TABLEFLOW_ENABLED,
    backendFieldName: CLUSTER_WORKLOADS_INPUTS_FIELDNAMES.IS_TABLEFLOW_ENABLED,
    icon: null,
    inputType: CHECKBOX_INPUT_TYPE,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM,
  });
}

export const CLUSTER_WORKLOAD_MONTHLY_INPUTS_CONFIG = [
  {
    displayName: CLUSTER_WORKLOADS_INPUTS_DISPLAY_NAMES.MONTHLY_INPUTS,
    backendFieldName: CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.SP_KAFKA_FORM,
    inputTransformationFunc: clusterMonthlyDataInputTransformationFunc,
  },
  {
    displayName: CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
    backendFieldName: CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.SP_KSQLDB_FORM,
    inputTransformationFunc: clusterMonthlyDataInputTransformationFunc,
  },
  {
    displayName: CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
    backendFieldName: CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.SP_CONNECTORS_FORM,
    inputTransformationFunc: connectorsMonthlyDataInputTransformationFunc,
  },
  {
    displayName: CLUSTER_AVAILABLE_CONNECTORS_LIST,
    backendFieldName: CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME,
    displayNameCol: CONNECTOR_LABEL,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.SP_CONNECTORS_FORM,
    parentFieldsInGroup: [
      CLUSTER_RESOURCES_GRID_FIELDNAMES.CLUSTER_TYPE,
      CLUSTER_RESOURCES_GRID_FIELDNAMES.PROVIDER,
      CLUSTER_RESOURCES_GRID_FIELDNAMES.NETWORKING_TYPE,
    ],
  },
  {
    displayName: CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST,
    backendFieldName: CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST_BACKEND_NAME,
    displayNameCol: CLUSTER_NETWORKING_TYPE_LABEL_BACKEND_NAME,
    belongingForm: FORM_NAMES.CLUSTER_WORKLOAD.SP_CONNECTORS_FORM,
    parentFieldsInGroup: [
      CLUSTER_RESOURCES_GRID_FIELDNAMES.CLUSTER_TYPE,
      CLUSTER_RESOURCES_GRID_FIELDNAMES.PROVIDER,
    ],
  },
];

export const CLUSTER_WORKLOAD_DETAILS_CONFIG = [
  CLUSTER_WORKLOAD_ENABLE_DISABLE_CONFIG,
  ...CLUSTER_WORKLOAD_DETAILS_INPUT_CONFIG,
  ...CLUSTER_WORKLOAD_MONTHLY_INPUTS_CONFIG,
];

export const CLUSTER_WORKLOAD_DETAILS_CONFIG_MAP = convertConfigArrayToMap(
  CLUSTER_WORKLOAD_DETAILS_CONFIG
);
